import { useMemo } from 'react'
import { decodeMetar } from '@skyweather/weather-core'
import { ageText } from './format.js'
import type { SiteUnits, SourceReading } from './types.js'

interface Props {
  readings: SourceReading[]
  units: SiteUnits
  timeZone: string
  now: number
  showFlightCategory?: boolean
  showRemarks?: boolean
}

/** Remarks shown: the most a TV-sized screen has room for without clipping a line. */
export const MAX_REMARKS = 8

/** The field's newest airport report: from an on-site AWOS if the site has one, else the national feed. */
export function latestFieldReport(readings: SourceReading[]): SourceReading | null {
  const airport = readings
    .filter((r) => (r.descriptor.kind === 'awos-bridge' || r.descriptor.kind === 'noaa-metar') && r.observation?.raw)
    .sort((a, b) => (b.observation?.observedAt ?? 0) - (a.observation?.observedAt ?? 0))
  return airport[0] ?? null
}

/**
 * The most current airport report, always on screen in full and in plain words, whatever feed is shown
 * above it, under the screen's one observations header. It names its own source in small type, since it can be a
 * different source from the feed above (Kyle, 2026-10-02: "every single thing on a metar needs to be here in plain data, and let's
 * display the most current metar on the screen as well"). The flight category uses the conventional pilot
 * colours, which is how pilots read it at a glance (Kyle asked for IFR/VFR on screen the same day).
 */
export function FieldReport({ readings, units, timeZone, now, showFlightCategory = true, showRemarks = true }: Props) {
  const r = latestFieldReport(readings)
  const raw = r?.observation?.raw ?? null
  // Decoding depends only on the report (and the clock's day for its date), not the ticking second.
  const decoded = useMemo(
    () => (raw ? decodeMetar(raw, { now: r?.observation?.observedAt ?? now, timeZone, units }) : null),
    [raw, timeZone, units.wind, units.temp, units.pressure, units.distance],
  )
  if (!r || !raw || !decoded) return null
  const o = r.observation
  const lines = decoded.lines.filter((l) => l.key !== 'station')
  return (
    <section className="fieldreport" aria-label="Latest field report">
      <div className="fieldreport__head">
        <span className="fieldreport__src">{r.descriptor.label}</span>
        <div className="fieldreport__meta">
          {showFlightCategory && decoded.flightCategory && (
            <span className={`fltcat fltcat--${decoded.flightCategory.toLowerCase()}`} title={decoded.flightCategoryReason}>
              {decoded.flightCategory}
            </span>
          )}
          <span className="fieldreport__age">{o ? ageText(o.observedAt, now) : ''}</span>
        </div>
      </div>
      <div className="fieldreport__top">
        <code className="fieldreport__raw">{raw}</code>
        {showFlightCategory && decoded.flightCategory && <span className="fieldreport__why">{decoded.flightCategory}: {decoded.flightCategoryReason}</span>}
      </div>
      <dl className="fieldreport__lines">
        {lines.map((l) => (
          <div key={l.key}>
            <dt>{l.label}</dt>
            <dd>{l.value}</dd>
          </div>
        ))}
      </dl>
      {showRemarks && decoded.remarks.length > 0 && (
        <div className="fieldreport__remarks">
          <h3>Remarks</h3>
          <ul>
            {decoded.remarks.slice(0, MAX_REMARKS).map((rm, i) => (
              <li key={`${rm.code}-${i}`}>
                <code>{rm.code}</code> {rm.meaning}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
